import { useMemo, useState } from "react";
import { usePatientContext } from "../../../../classes/contexts/PatientContext";
import { useUser } from "../../../../classes/contexts/UserContext";
import {
  filterByDateRange,
  formatDateWithTimeInZone,
} from "../../../../classes/utils/DateUtils";
import { filterBySearchTerm } from "../../../../classes/utils/SearchUtils";
import AddNew from "../../../../components/addNew/AddNew";
import { ErrorMessage } from "../../../../components/errorMessage/ErrorMessage";
import FilterAndActionBar from "../../../../components/filterAndActionBar.js/FilterAndActionBar";
import WidgetLoader from "../../../../components/widgetLoader/WidgetLoader";
import useTranslation from "../../../../hooks/i18n/useTranslation";
import "../common/PatientWidgetLayout.css";
import usePatientWidgetControlsOffset from "../common/usePatientWidgetControlsOffset";
import ReportItem from "./ReportItem";
import "./Reports.css";

const Reports = () => {
  const { t } = useTranslation();
  const { patientData, reports, reportsLoading, reportsError, refreshReports } =
    usePatientContext();
  const { dateSettings } = useUser();
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({ start: null, end: null });

  const handleSearch = term => {
    setSearchTerm(term);
  };

  const handleDateRangeChange = (start, end) => {
    setDateRange({ start, end });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setDateRange({ start: null, end: null });
  };

  const filteredReports = useMemo(() => {
    let filtered = reports || [];

    // Filter by search term
    filtered = filterBySearchTerm(
      filtered,
      r =>
        `${r.title || ""} ${
          formatDateWithTimeInZone(r.date, undefined, {
            locale: dateSettings?.locale,
          }) || ""
        }`,
      searchTerm
    );

    // Filter by date range
    if (dateRange.start || dateRange.end) {
      filtered = filterByDateRange(
        filtered,
        item => item.date,
        dateRange.start,
        dateRange.end
      );
    }

    return filtered;
  }, [reports, searchTerm, dateRange]);

  const renderContent = () => {
    if (reportsLoading) {
      return <WidgetLoader isLoading={true} />;
    }

    if (reportsError) {
      return <ErrorMessage message={t("215")} />;
    }

    if (!filteredReports || filteredReports.length === 0) {
      return <AddNew elementTitle={t("189")} onClick={null} />;
    }

    return (
      <div className="patient-reports-list">
        {filteredReports.map(report => (
          <ReportItem
            key={report._id}
            report={report}
            patientId={patientData?._id}
            onReportDeleted={refreshReports}
          />
        ))}
      </div>
    );
  };

  const { controlsRef, shellStyle } = usePatientWidgetControlsOffset();

  return (
    <div className="patient-reports patient-widget-shell" style={shellStyle}>
      <div className="patient-reports-content patient-widget-content">
        {renderContent()}
      </div>

      <div
        className="patient-reports-controls patient-widget-controls"
        ref={controlsRef}
      >
        <FilterAndActionBar
          onSearch={handleSearch}
          onDateRangeChange={handleDateRangeChange}
          onClear={clearFilters}
          onAdd={null}
          searchPlaceholder={t("5")}
          value={searchTerm}
        />
      </div>
    </div>
  );
};

export default Reports;
